/**
 * CaregiverSecurityPage (CONTRACTS §6): password update for the caregiver
 * via useAuth().updatePassword, with the shared password policy from the
 * auth feature. Role checks stay server-side; this page never trusts
 * client input.
 */
import { useState } from 'react'
import { Banner, Button, Card, PasswordField, useToast } from '@/components/ui'
import { LockIcon, ShieldIcon } from '@/components/icons'
import { useAuth } from '@/features/auth'
import { validatePassword, validatePasswordConfirm, PASSWORD_POLICY_HINT } from '@/features/auth/validation'
import { errorMessage } from '@/features/medication'
import './caregiver.css'

export default function CaregiverSecurityPage() {
  const { updatePassword } = useAuth()
  const { showToast } = useToast()

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const [confirmError, setConfirmError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [done, setDone] = useState(false)

  const submit = async () => {
    if (saving) return
    const nextPasswordError = validatePassword(password)
    const nextConfirmError = validatePasswordConfirm(password, confirm)
    setPasswordError(nextPasswordError)
    setConfirmError(nextConfirmError)
    if (nextPasswordError || nextConfirmError) return
    setSaving(true)
    try {
      await updatePassword(password)
      setPassword('')
      setConfirm('')
      setDone(true)
      showToast({ message: 'Je wachtwoord is bijgewerkt.', tone: 'success' })
    } catch (error) {
      showToast({ message: errorMessage(error), tone: 'error' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="caregiver-page">
      <header>
        <h1 className="caregiver-section-title">Beveiliging</h1>
        <p className="caregiver-muted">
          Beheer de beveiliging van je begeleidersaccount.
        </p>
      </header>

      {done ? (
        <Banner tone="info">Je wachtwoord is gewijzigd. Andere apparaten moeten opnieuw inloggen.</Banner>
      ) : null}

      <Card padding="md">
        <div className="caregiver-form">
          <h2 className="caregiver-section-title">
            <LockIcon size={18} /> Wachtwoord wijzigen
          </h2>
          <PasswordField
            label="Nieuw wachtwoord"
            value={password}
            onChange={setPassword}
            error={passwordError ?? undefined}
            hint={PASSWORD_POLICY_HINT}
            autoComplete="new-password"
            required
          />
          <PasswordField
            label="Herhaal nieuw wachtwoord"
            value={confirm}
            onChange={setConfirm}
            error={confirmError ?? undefined}
            autoComplete="new-password"
            required
          />
          <div className="caregiver-actions">
            <Button variant="primary" loading={saving} onClick={() => void submit()}>
              Wachtwoord bijwerken
            </Button>
          </div>
        </div>
      </Card>

      <Card padding="md">
        <div className="caregiver-section">
          <h2 className="caregiver-section-title">
            <ShieldIcon size={18} /> Zo blijven gegevens beschermd
          </h2>
          <p className="caregiver-muted">
            Je rol (begeleider) en je koppelingen met cliënten worden op de server gecontroleerd,
            nooit op dit apparaat. Elke keer dat je gegevens van een cliënt bekijkt of wijzigt,
            wordt dat vastgelegd in het auditlog. Zodra een koppeling eindigt, stopt je toegang
            direct.
          </p>
        </div>
      </Card>
    </div>
  )
}
